import { memo, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Outlet } from 'react-router-dom';
// Common
import Layout from 'components/Common/Layout/Layout';
import CheckAuth from 'components/Common/CheckAuth/CheckAuth';
import CheckSession from 'components/Common/CheckSession/CheckSession';
import ComponentErrorWrapper from 'components/Common/ComponentErrorWraper/ComponentErrorWrapper';
import PageLoader from 'components/Common/PageLoader/PageLoader';
// Views - Lazy loaded for code splitting
const Accounts = lazy(() => import('views/Accounts/Accounts'));
const AccountDetails = lazy(() => import('views/AccountDetails/AccountDetails'));
const Customer = lazy(() => import('views/Customer/Customer'));
const Company = lazy(() => import('views/Company/Company'));
const DriverDetails = lazy(() => import('views/DriverDetails/DriverDetails'));
const Drivers = lazy(() => import('views/Drivers/Drivers'));
const ManagerDetails = lazy(() => import('views/ManagerDetails/ManagerDetails'));
const Managers = lazy(() => import('views/Managers/Managers'));
const TruckDetails = lazy(() => import('views/TruckDetails/TruckDetails'));
const Trucks = lazy(() => import('views/Trucks/Trucks'));
const Orders = lazy(() => import('views/Orders/Orders'));
const OrderDetails = lazy(() => import('views/OrderDetails/OrderDetails'));
const Invoices = lazy(() => import('views/Invoices/Invoices'));
const InvoiceDetails = lazy(() => import('views/InvoiceDetails/InvoiceDetails'));
const Vehicles = lazy(() => import('views/Vehicles/Vehicles'));
const VehicleDetails = lazy(() => import('views/VehicleDetails/VehicleDetails'));
const Auction = lazy(() => import('views/Auction/Auction'));
const AuctionedVehicleDetails = lazy(() => import('views/AuctionedVehicleDetails/AuctionedVehicleDetails'));
const Dashboard = lazy(() => import('views/Dashboard/Dashboard'));
const Map = lazy(() => import('views/Map/Map'));
const NotFound = lazy(() => import('views/NotFound/NotFound'));
const Recovery = lazy(() => import('views/Recovery/Recovery'));
const Settings = lazy(() => import('views/Settings/Settings'));
const DutyStatus = lazy(() => import('views/DutyStatus/DutyStatus'));
const Resources = lazy(() => import('views/Resources/Resources'));
const SignIn = lazy(() => import('views/SignIn/SignIn'));
const SignUp = lazy(() => import('views/SignUp/SignUp'));
const Status = lazy(() => import('views/Status/Status'));
const Widget = lazy(() => import('views/Widget/Widget'));
const PublicStoredVehicles = lazy(() => import('views/PublicStoredVehicles/PublicStoredVehicles'));
const PublicTruckInspection = lazy(() => import('views/PublicTruckInspection/PublicTruckInspection'));
const PublicKcmoStorageInspection = lazy(() => import('views/PublicKcmoStorageInspection/PublicKcmoStorageInspection'));

const App = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path='public/stored-vehicles'
          element={
            <ComponentErrorWrapper
              children={
                <Suspense fallback={<PageLoader />}>
                  <PublicStoredVehicles />
                </Suspense>
              }
            />
          }
        />
        <Route
          path='public/storage-facility-inspection/:id'
          element={
            <ComponentErrorWrapper
              children={
                <Suspense fallback={<PageLoader />}>
                  <PublicKcmoStorageInspection />
                </Suspense>
              }
            />
          }
        />
        <Route
          path='public/truck-inspection/:id'
          element={
            <ComponentErrorWrapper
              children={
                <Suspense fallback={<PageLoader />}>
                  <PublicTruckInspection />
                </Suspense>
              }
            />
          }
        />
        <Route path='/' element={<ComponentErrorWrapper children={<Layout />} />}>
          <Route path='duty_status' element={<CheckAuth><Suspense fallback={<PageLoader />}><DutyStatus /></Suspense></CheckAuth>}/>
          <Route path='dashboard' element={<CheckAuth><Suspense fallback={<PageLoader />}><Dashboard /></Suspense></CheckAuth>}/>
          <Route path='invoices' element={<CheckAuth><ComponentErrorWrapper children={<Suspense fallback={<PageLoader />}><Invoices /></Suspense>} /></CheckAuth>}>
            <Route path=':id' element={<CheckAuth><Suspense fallback={<PageLoader />}><InvoiceDetails /></Suspense></CheckAuth>}/>
          </Route>
          <Route path='vehicles' element={<CheckAuth><ComponentErrorWrapper children={<Suspense fallback={<PageLoader />}><Vehicles /></Suspense>} /></CheckAuth>}>
            <Route path=':id' element={<CheckAuth><Suspense fallback={<PageLoader />}><VehicleDetails /></Suspense></CheckAuth>} />
          </Route>
          <Route path='auction' element={<CheckAuth><ComponentErrorWrapper children={<Suspense fallback={<PageLoader />}><Auction /></Suspense>} /></CheckAuth>}>
            <Route path=':id' element={<CheckAuth><Suspense fallback={<PageLoader />}><AuctionedVehicleDetails /></Suspense></CheckAuth>} />
          </Route>
          <Route path='accounts' element={<CheckAuth><ComponentErrorWrapper children={<Suspense fallback={<PageLoader />}><Accounts /></Suspense>} /></CheckAuth>}>
            <Route path=':id' element={<CheckAuth><Suspense fallback={<PageLoader />}><AccountDetails /></Suspense></CheckAuth>} />
          </Route>
          <Route path='company' element={<CheckAuth><Suspense fallback={<PageLoader />}><Company /></Suspense></CheckAuth>}>
            <Route path='drivers' element={<CheckAuth><Suspense fallback={<PageLoader />}><Drivers /></Suspense></CheckAuth>}>
              <Route path=':id' element={<CheckAuth><Suspense fallback={<PageLoader />}><DriverDetails /></Suspense></CheckAuth>} />
            </Route>
            <Route path='managers' element={<CheckAuth><Suspense fallback={<PageLoader />}><Managers /></Suspense></CheckAuth>}>
              <Route path=':id' element={<CheckAuth><Suspense fallback={<PageLoader />}><ManagerDetails /></Suspense></CheckAuth>} />
            </Route>
            <Route path='trucks' element={<CheckAuth><Suspense fallback={<PageLoader />}><Trucks /></Suspense></CheckAuth>}>
              <Route path=':id' element={<CheckAuth><Suspense fallback={<PageLoader />}><TruckDetails /></Suspense></CheckAuth>} />
            </Route>
          </Route>
          <Route path='orders' element={<CheckAuth><ComponentErrorWrapper children={<Suspense fallback={<PageLoader />}><Orders /></Suspense>} /></CheckAuth>}>
            <Route path=':id' element={<CheckAuth><Suspense fallback={<PageLoader />}><OrderDetails /></Suspense></CheckAuth>} />
          </Route>
          <Route path='map' element={<CheckAuth><ComponentErrorWrapper children={<Suspense fallback={<PageLoader />}><Map /></Suspense>} /></CheckAuth>} />
          <Route path='settings' element={<CheckAuth><Suspense fallback={<PageLoader />}><Settings /></Suspense></CheckAuth>}>
            <Route path=':id' element={<Outlet />}>
              <Route path='stripe-connect/success' element={<Outlet />} />
              <Route path='stripe-connect/reload' element={<Outlet />} />
              <Route path='stripe-subscription/success' element={<Outlet />} />
              <Route path='stripe-subscription/cancel' element={<Outlet />} />
            </Route>
          </Route>
          <Route path='resources' element={<CheckAuth><Suspense fallback={<PageLoader />}><Resources /></Suspense></CheckAuth>} />
          <Route path='status' element={<CheckAuth><Suspense fallback={<PageLoader />}><Status /></Suspense></CheckAuth>} />
          <Route path='recovery' element={<CheckSession><Suspense fallback={<PageLoader />}><Recovery /></Suspense></CheckSession>} />
          <Route path='sign_in' element={<CheckSession><Suspense fallback={<PageLoader />}><SignIn /></Suspense></CheckSession>} />
          <Route path='sign_up' element={<CheckSession><Suspense fallback={<PageLoader />}><SignUp /></Suspense></CheckSession>} />
          <Route path='c/:customer_token/o/:order_id' element={<ComponentErrorWrapper children={<Suspense fallback={<PageLoader />}><Customer /></Suspense>} />}/>
          <Route path='c/:company_id/widget' element={<ComponentErrorWrapper children={<Suspense fallback={<PageLoader />}><Widget /></Suspense>} />} />
          <Route path='add/*' element={<Outlet/>} />
          <Route path='*' element={<Suspense fallback={<PageLoader />}><NotFound /></Suspense>} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default memo(App);